import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Globe, AlertTriangle, CheckCircle2, XCircle, Info } from "lucide-react";
import { applySiteConfig } from "@/hooks/useSiteConfig";
import { detectTags } from "@/lib/tagDetector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SiteConfigPanel() {
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    site_title: "",
    site_description: "",
    og_image: "",
    head_scripts: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/site/config`);
        setForm({
          site_title: data.site_title || "",
          site_description: data.site_description || "",
          og_image: data.og_image || "",
          head_scripts: data.head_scripts || "",
        });
      } catch {
        toast.error("Failed to load site config");
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const onChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/site-config`, form);
      toast.success("Site config saved");
      // Apply immediately to the current page so changes are visible without reload.
      applySiteConfig(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const headCharCount = form.head_scripts.length;
  const detectedTags = useMemo(
    () => detectTags(form.head_scripts),
    [form.head_scripts]
  );

  if (!loaded) {
    return (
      <div className="bg-white border-2 border-[#1A1A1A] shadow-[5px_5px_0px_#1A1A1A] p-6 mb-10">
        <div className="font-serif-display italic text-[#6B6A68]">
          Loading site config…
        </div>
      </div>
    );
  }

  return (
    <section
      className="bg-white border-2 border-[#1A1A1A] shadow-[5px_5px_0px_#1A1A1A] p-6 mb-10"
      data-testid="admin-site-config-section"
    >
      <div className="flex items-center gap-2 mb-1">
        <Globe className="w-4 h-4 text-[#EB5E28]" />
        <span className="font-sans-ui text-[10px] uppercase tracking-[0.24em] font-bold text-[#EB5E28]">
          Masthead &amp; SEO
        </span>
      </div>
      <h2 className="font-serif-display text-2xl font-semibold mb-1">
        Site &amp; scripts
      </h2>
      <p className="font-sans-ui text-sm text-[#6B6A68] mb-5">
        Set the page title, meta description, Open Graph image, and inject
        third-party scripts (Analytics, Ads, Pixel, etc.) into the document head.
      </p>

      <form onSubmit={onSubmit} className="space-y-5">
        <Field
          label="Site title"
          help="Shown in browser tabs and search results."
        >
          <input
            type="text"
            value={form.site_title}
            onChange={onChange("site_title")}
            maxLength={500}
            data-testid="site-title-input"
            className="w-full border-2 border-[#1A1A1A] bg-[#F4F1EA] px-3 py-2.5 font-sans-ui text-base focus:outline-none focus:ring-2 focus:ring-[#EB5E28]"
            placeholder="Fraseology — a British idiom guessing game"
          />
        </Field>

        <Field
          label="Meta description"
          help="Shown as the snippet in Google/Bing. Aim for 150–160 characters."
        >
          <textarea
            rows={3}
            value={form.site_description}
            onChange={onChange("site_description")}
            maxLength={500}
            data-testid="site-description-input"
            className="w-full border-2 border-[#1A1A1A] bg-[#F4F1EA] px-3 py-2.5 font-sans-ui text-sm focus:outline-none focus:ring-2 focus:ring-[#EB5E28] resize-y"
            placeholder="Rearrange the scattered letters. Recover the phrase."
          />
          <p className="mt-1 font-sans-ui text-[10px] text-[#6B6A68] tabular-nums">
            {form.site_description.length} / 500
          </p>
        </Field>

        <Field
          label="Open Graph image URL"
          help="1200×630 JPG/PNG. Used by social sharing previews."
        >
          <input
            type="url"
            value={form.og_image}
            onChange={onChange("og_image")}
            maxLength={500}
            data-testid="site-og-image-input"
            className="w-full border-2 border-[#1A1A1A] bg-[#F4F1EA] px-3 py-2.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#EB5E28]"
            placeholder="https://example.com/social-card.jpg"
          />
        </Field>

        <Field
          label="Head scripts / tags"
          help="Raw HTML appended to the document <head>. Paste Google Analytics, Google Ads, Meta Pixel, AdSense verification, custom <meta> tags, etc."
        >
          <div className="mb-2 flex items-start gap-2 border-2 border-[#EB5E28]/40 bg-[#EB5E28]/10 p-2.5">
            <AlertTriangle className="w-3.5 h-3.5 text-[#EB5E28] shrink-0 mt-0.5" />
            <span className="font-sans-ui text-[11px] text-[#1A1A1A] leading-relaxed">
              Anything you paste here executes in every visitor's browser. Only
              paste snippets from trusted sources (GA4, Tag Manager, AdSense, Pixel).
            </span>
          </div>
          <textarea
            rows={10}
            value={form.head_scripts}
            onChange={onChange("head_scripts")}
            maxLength={20000}
            spellCheck={false}
            data-testid="site-head-scripts-input"
            className="w-full border-2 border-[#1A1A1A] bg-[#F4F1EA] px-3 py-2.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-[#EB5E28] resize-y"
            placeholder={`<!-- Google tag (gtag.js) -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n  gtag('config', 'G-XXXXXXXXXX');\n</script>`}
          />
          <p className="mt-1 font-sans-ui text-[10px] text-[#6B6A68] tabular-nums">
            {headCharCount.toLocaleString()} / 20,000
          </p>
          <TagDetectorPanel
            empty={headCharCount === 0}
            tags={detectedTags}
          />
        </Field>

        <div>
          <button
            type="submit"
            disabled={saving}
            data-testid="site-config-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A1A1A] text-[#F4F1EA] border-2 border-[#1A1A1A] font-sans-ui font-bold uppercase tracking-[0.15em] text-xs shadow-[3px_3px_0px_#EB5E28] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_#EB5E28] active:translate-y-0 active:shadow-[0px_0px_0px_#EB5E28] transition-all disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save site config
          </button>
        </div>
      </form>
    </section>
  );
}

function Field({ label, help, children }) {
  return (
    <div>
      <label className="block font-sans-ui text-[10px] uppercase tracking-[0.24em] font-semibold text-[#6B6A68] mb-1.5">
        {label}
      </label>
      {children}
      {help && (
        <p className="mt-1.5 font-sans-ui text-[11px] italic text-[#6B6A68]">
          {help}
        </p>
      )}
    </div>
  );
}

function TagDetectorPanel({ empty, tags }) {
  if (empty) {
    return (
      <div
        className="mt-3 border-2 border-dashed border-[#1A1A1A]/20 bg-[#F4F1EA]/50 p-3 flex items-start gap-2"
        data-testid="tag-detector"
        data-state="empty"
      >
        <Info className="w-3.5 h-3.5 text-[#6B6A68] shrink-0 mt-0.5" />
        <p className="font-sans-ui text-[11px] text-[#6B6A68] leading-relaxed">
          Paste a snippet above and we'll confirm which tag it is (GA4, AdSense,
          Meta Pixel, etc.) before you save.
        </p>
      </div>
    );
  }
  if (tags.length === 0) {
    return (
      <div
        className="mt-3 border-2 border-[#842029] bg-[#F8D7DA] p-3 flex items-start gap-2"
        data-testid="tag-detector"
        data-state="unknown"
      >
        <XCircle className="w-4 h-4 text-[#842029] shrink-0 mt-0.5" />
        <div className="font-sans-ui text-[12px] text-[#842029] leading-relaxed">
          <b>No known tag detected.</b> The snippet will still save, but we
          couldn't find a recognisable GA4 / GTM / AdSense / Pixel ID — check
          for typos or incomplete paste before leaving this page.
        </div>
      </div>
    );
  }
  return (
    <div
      className="mt-3 border-2 border-[#0F5132] bg-[#D1E7DD] p-3"
      data-testid="tag-detector"
      data-state="detected"
    >
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 className="w-4 h-4 text-[#0F5132]" />
        <span className="font-sans-ui text-[10px] uppercase tracking-[0.24em] font-bold text-[#0F5132]">
          {tags.length} tag{tags.length === 1 ? "" : "s"} detected
        </span>
      </div>
      <ul className="space-y-1" data-testid="tag-detector-list">
        {tags.map((t) => (
          <li
            key={t.key}
            data-testid={`tag-detected-${t.key}`}
            className="flex items-center justify-between gap-3 font-sans-ui text-xs"
          >
            <span className="text-[#0F5132] font-semibold">
              {t.legacy && (
                <span className="mr-1.5 px-1.5 py-0.5 bg-[#842029]/10 text-[#842029] text-[9px] uppercase tracking-[0.2em] font-bold">
                  Legacy
                </span>
              )}
              {t.label}
            </span>
            {t.id ? (
              <code className="font-mono text-[11px] bg-white/70 px-1.5 py-0.5 border border-[#0F5132]/30 text-[#0F5132]">
                {t.id}
              </code>
            ) : (
              <span className="font-mono text-[11px] text-[#6B6A68] italic">
                loader present
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
